import React, { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { MapProvider } from "./components/MapContext";
import GlobalStyles from "./components/GlobalStyles";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./components/HomePage";
import MapLayout from "./components/MapLayout";
import styled from "styled-components";
import InstallBanner from "./components/InstallBanner";
import SiteFooter from "./components/legal/SiteFooter";
import useRestaurants from "./hooks/useRestaurants";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import AppMenu from "./components/AppMenu";
import LangToggle from "./components/LangToggle";

const Map = lazy(() => import("./components/Map"));
const MapPage = lazy(() => import("./components/MapPage"));
const Favourites = lazy(() => import("./components/Favourites"));
const Profile = lazy(() => import("./components/Profile"));
const RestoInfo = lazy(() => import("./components/RestoInfo"));
const FoodPrint = lazy(() => import("./components/FoodPrint"));
const About = lazy(() => import("./components/About"));
const LegalPage = lazy(() => import("./components/legal/LegalPage"));
const FriendsAdded = lazy(() =>
  import("./components/friedns-and-users/FriendsAdded")
);
const FriendRequestInbox = lazy(() =>
  import("./components/friedns-and-users/FriendRequestInbox")
);
const Friends = lazy(() => import("./components/friedns-and-users/Friends"));
const SharedListPage = lazy(() => import("./components/SharedListPage"));
const SharedListPreview = lazy(() => import("./components/SharedListPreview"));
const PoolPage = lazy(() => import("./components/PoolPage"));
const DinnerPlanning = lazy(() => import("./components/DinnerPlanning"));

const NEED_RESTAURANTS = new Set([
  "/",
  "/homePage",
  "/map",
  "/profile",
  "/restoInfo",
]);

function RestaurantsWarmup() {
  const { pathname } = useLocation();
  if (!NEED_RESTAURANTS.has(pathname)) return null;
  return <RestaurantsWarmupInner />;
}

function RestaurantsWarmupInner() {
  useRestaurants();
  return null;
}

const App = () => {
  const location = useLocation();
  const path = location.pathname;
  const showFullMap = path === "/map";
  const [mapArmed, setMapArmed] = useState(showFullMap);
  const hideChrome =
    path.startsWith("/list/") ||
    path === "/pool" ||
    path.startsWith("/pool/");
  const immersive = path === "/restoInfo";
  const legalDoc =
    path === "/privacy" || path === "/cookies" || path === "/terms";
  const showLegalStrip = !hideChrome && !showFullMap && !immersive && !legalDoc;
  const { isDark } = useTheme();

  useEffect(() => {
    if (showFullMap) setMapArmed(true);
  }, [showFullMap]);

  return (
    <MapProvider>
      <GlobalStyles />
      <Shell
        className={isDark ? "theme-dark" : "theme-light"}
        $hideChrome={hideChrome}
        $dark={isDark}
        $map={showFullMap}
      >
        {!hideChrome && (
          <TopBar $dark={isDark} $immersive={immersive}>
            <Header />
          </TopBar>
        )}
        {hideChrome && (
          <FloatMenu>
            <LangToggle variant="float" />
            <AppMenu variant="float" />
          </FloatMenu>
        )}

        <Body $map={showFullMap} $hideChrome={hideChrome} data-app-scroll>
          <RestaurantsWarmup />

          <Discover $map={showFullMap}>
            {showFullMap && (
              <DiscoverSide>
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    <Route element={<MapLayout />}>
                      <Route path="/map" element={<MapPage />} />
                    </Route>
                  </Routes>
                </Suspense>
              </DiscoverSide>
            )}
            <MapSlot $hidden={!showFullMap} $full={showFullMap} aria-hidden={!showFullMap}>
              {mapArmed ? (
                <Suspense fallback={<RouteFallback $fill />}>
                  <Map hideSearchForm={!showFullMap} isVisible={showFullMap} />
                </Suspense>
              ) : null}
            </MapSlot>
          </Discover>

          {!showFullMap && (
            <Main $padTop={!hideChrome && !immersive} $wide={hideChrome}>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route element={<MapLayout />}>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/homePage" element={<HomePage />} />
                  </Route>
                  <Route path="/favourites" element={<Favourites />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/restoInfo" element={<RestoInfo />} />
                  <Route path="/friendsAdded" element={<FriendsAdded />} />
                  <Route path="/friend-requests" element={<FriendRequestInbox />} />
                  <Route path="/friends" element={<Friends />} />
                  <Route path="/FoodPrint" element={<FoodPrint />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/privacy" element={<LegalPage />} />
                  <Route path="/cookies" element={<LegalPage />} />
                  <Route path="/terms" element={<LegalPage />} />
                  <Route path="/list/:shareId" element={<SharedListPage />} />
                  <Route path="/shared/:shareId" element={<SharedListPreview />} />
                  <Route path="/pool/:code" element={<PoolPage />} />
                  <Route path="/pool" element={<PoolPage />} />
                  <Route path="/plan" element={<DinnerPlanning />} />
                </Routes>
              </Suspense>
              {showLegalStrip && <SiteFooter />}
            </Main>
          )}
        </Body>

        {!hideChrome && (
          <TabBar $dark={isDark}>
            <Footer />
          </TabBar>
        )}
        {!hideChrome && <InstallBanner />}
      </Shell>
    </MapProvider>
  );
};

export default () => (
  <Router>
    <LanguageProvider>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </LanguageProvider>
  </Router>
);

const Shell = styled.div`
  min-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  position: relative;
  background: ${(p) =>
    p.$dark ? "var(--surface-dark)" : "var(--color-cream)"};
  color: ${(p) => (p.$dark ? "var(--text-on-dark)" : "var(--text-primary)")};
  --filter-bg: ${(p) =>
    p.$dark ? "var(--surface-dark-2)" : "var(--surface-light)"};
  --filter-fg: ${(p) => (p.$dark ? "var(--text-on-dark)" : "var(--color-ink)")};
  --filter-border: ${(p) =>
    p.$dark ? "var(--border-on-dark)" : "var(--border-subtle)"};

  @media (min-width: 1024px) {
    max-width: none;
  }
`;

const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 30;
  min-height: var(--header-h);
  display: flex;
  align-items: center;
  padding: 10px 18px;
  color: ${(p) =>
    p.$dark ? "var(--text-on-dark)" : "var(--color-ink)"};
  background: ${(p) =>
    p.$dark ? "rgba(23, 23, 23, 0.92)" : "rgba(250, 247, 241, 0.92)"};
  border-bottom: 1px solid
    ${(p) => (p.$dark ? "var(--border-on-dark)" : "var(--border-subtle)")};

  @media (min-width: 1024px) {
    min-height: var(--desktop-header-h);
    padding: 14px 32px;
  }

  ${(p) =>
    p.$immersive
      ? `@media (max-width: 1023px) { display: none; }`
      : ""}
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: ${(p) =>
    p.$hideChrome ? "0" : "calc(var(--tabbar-h) + 20px)"};
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    padding-bottom: ${(p) => (p.$map ? "0" : "32px")};
  }
`;

const Discover = styled.div`
  ${(p) =>
    p.$map
      ? `
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    width: 100%;
  `
      : `
    flex: 0 0 0;
    width: 0;
    height: 0;
    min-height: 0;
    overflow: visible;
    pointer-events: none;
  `}

  @media (min-width: 1024px) {
    ${(p) =>
      p.$map
        ? `
      display: grid;
      grid-template-columns: minmax(300px, 380px) 1fr;
      gap: 20px;
      padding: 8px 32px 24px;
      max-width: none;
    `
        : ""}
  }
`;

const DiscoverSide = styled.div`
  flex: 0 0 auto;
  min-height: 0;

  @media (min-width: 1024px) {
    overflow-y: auto;
    padding-right: 4px;
  }
`;

const MapSlot = styled.div`
  width: ${(p) => (p.$hidden ? "240px" : "calc(100% - 32px)")};
  margin: ${(p) => (p.$hidden ? "0" : "8px auto 0")};
  height: ${(p) => (p.$hidden ? "200px" : "58vh")};
  min-height: ${(p) => (p.$hidden ? "200px" : "280px")};
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: ${(p) => (p.$hidden ? "0" : "1px solid var(--border-on-dark)")};
  background: var(--surface-dark-2);
  position: ${(p) => (p.$hidden ? "fixed" : "relative")};
  left: ${(p) => (p.$hidden ? "-240vw" : "auto")};
  top: ${(p) => (p.$hidden ? "0" : "auto")};
  z-index: 0;
  pointer-events: ${(p) => (p.$hidden ? "none" : "auto")};
  flex: ${(p) => (p.$full && !p.$hidden ? "1 1 auto" : "0 0 auto")};

  @media (min-width: 1024px) {
    width: ${(p) => (p.$hidden ? "240px" : "100%")};
    height: ${(p) => (p.$hidden ? "200px" : "100%")};
    min-height: ${(p) => (p.$hidden ? "200px" : "560px")};
    margin: ${(p) => (p.$hidden ? "0" : "0")};
    border: ${(p) => (p.$hidden ? "0" : "1px solid var(--border-subtle)")};
    background: var(--surface-light);
  }
`;

const RouteFallback = styled.div`
  min-height: ${(p) => (p.$fill ? "100%" : "40vh")};
  height: ${(p) => (p.$fill ? "100%" : "auto")};
  background: var(--bg);
`;

const Main = styled.main`
  width: 100%;
  position: relative;
  z-index: 1;
  padding-top: ${(p) => (p.$padTop ? "8px" : "0")};
  max-width: ${(p) => (p.$wide ? "none" : "var(--content-max)")};
  margin: 0 auto;
`;

const FloatMenu = styled.div`
  position: fixed;
  top: calc(12px + env(safe-area-inset-top));
  right: 14px;
  z-index: 80;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TabBar = styled.footer`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  z-index: 40;
  min-height: var(--tabbar-h);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px calc(10px + env(safe-area-inset-bottom));
  background: ${(p) =>
    p.$dark ? "rgba(23, 23, 23, 0.96)" : "rgba(250, 247, 241, 0.94)"};
  border-top: 1px solid
    ${(p) => (p.$dark ? "var(--border-on-dark)" : "var(--border-subtle)")};

  @media (min-width: 1024px) {
    display: none;
  }
`;
